import { useMutation } from '@apollo/client'
import { GraphQLError } from 'graphql'
import { compose, test } from 'ramda'
import { useContext, useMemo, useState } from 'react'

import {
  AddSimpleProductsToCartDocument,
  AddSimpleProductsToCartMutation,
} from '../../../graphql/magento'
import useLogAndCaptureError from '../../../hooks/useLogAndCaptureError'
import { TokenContext } from '../../../layouts/context'
import { publishAddToCartEventForItems } from '../../../lib/analytics/cart'
import { useCartContext } from '../../../lib/cart'
import { logAndCaptureException } from '../../../utils/errorTools'
import { tapSuccessfulQueryResponse } from '../../../utils/graphqlTools'

const isProductNotAvailableError = compose(
  test(/Product that you are trying to add is not available/),
  (obj: GraphQLError) => obj.message,
)

export function useProtocolSimpleAddToCart(itemSku: string, itemQuantity: number) {
  const token = useContext(TokenContext)
  const { cart } = useCartContext()
  const [success, setSuccess] = useState(false)

  const onCompleted = (data: AddSimpleProductsToCartMutation) => {
    if (data?.addSimpleProductsToCart) {
      setSuccess(true)
    }
  }

  const [addSimpleProductsToCart, { loading, error }] = useMutation(
    AddSimpleProductsToCartDocument,
    {
      context: { token },
      onCompleted,
    },
  )

  useLogAndCaptureError(error)

  const errorMessage = useMemo(() => {
    if (!error) return undefined
    const catchableError = error.graphQLErrors.find(isProductNotAvailableError)
    if (!catchableError) {
      logAndCaptureException(error)
      return undefined
    }
    return catchableError.message
  }, [error])

  const onAdd = useMemo(() => {
    const cartId = cart?.id
    const sku = itemSku
    if (!cartId || !sku) {
      return undefined
    }
    return () => {
      setSuccess(false)
      return addSimpleProductsToCart({
        variables: {
          cartId,
          cartItems: [
            {
              data: {
                sku,
                quantity: itemQuantity,
                dfh_order_type: 1,
                dfh_autoship_interval: 1,
              },
            },
          ],
        },
      }).then(
        tapSuccessfulQueryResponse(
          publishAddToCartEventForItems({
            items: [
              {
                quantity: itemQuantity,
                sku,
              },
            ],
          }),
        ),
      )
    }
  }, [addSimpleProductsToCart, cart?.id, itemSku, itemQuantity])

  return { onAdd, loading, errorMessage, success }
}
